/**
 * "Two tools on connect" — the deferred-tools band.
 *
 * Answers the one objection a solution-aware ServiceNow developer brings to
 * every MCP server: a few hundred tools in `tools/list` floods the context
 * window and degrades tool selection, which is why the last one got
 * uninstalled. The answer is mechanical, not rhetorical — the catalogue is
 * deferred, so the connect handshake costs two tool definitions and the model
 * widens its own surface as it works.
 *
 * Verified in serac-labs/serac @ origin/main (2026-08-19), not asserted:
 *   - handlers/list-tools.ts — `SNOW_LAZY_TOOLS !== "false"` (deferred is the
 *     DEFAULT), and the returned list is META_TOOLS + only the tools this
 *     session enabled. A fresh session has enabled nothing, so `tools/list`
 *     answers with `tool_search` and `tool_execute`. Note the "+": the list
 *     GROWS with what the agent opens and those stay for the session, so the
 *     honest claim is a floor of two, not a flat cost — `deferred.body` and
 *     `deferred.consequence` both say it that way.
 *   - tools/meta/index.ts — `META_TOOLS` is exactly those two, in that order,
 *     and `tool_execute` refuses a tool that `tool_search` has not enabled.
 *   - The mechanism is the stdio path — the one this section sells (your own
 *     MCP client, your own machine). The HTTP transport registers its catalog
 *     non-deferred on purpose (transports/http-entry.ts), because the portal
 *     budgets tools client-side.
 *
 * The catalogue size appears exactly ONCE on this page: in `deferred.countLink`
 * via the `{{toolCount}}` interpolation. Both the number and the link the
 * reader is invited to check it against come from the same generated module,
 * `src/generated/catalog-counts.ts` — importing the URL rather than repeating
 * it is what makes it impossible for the count and its source to disagree.
 * Never hardcode either here.
 *
 * Every user-visible string comes from the `deferred` namespace and is rendered
 * as TEXT, not markup: keep the copy plain (no inline <code>/<strong>).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../serac';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { CATALOG_SOURCE_URLS } from '../../generated/catalog-counts';

export default function SectionDeferredTools() {
  const { t } = useTranslation('marketing');
  const isMobile = useIsMobile();
  // Hover AND keyboard focus, tracked separately: the global reset in
  // index.css strips the outline from `a:focus-visible` on the assumption that
  // each link brings its own ring, and this is the only interactive element in
  // the band.
  const [linkHot, setLinkHot] = useState(false);
  const [linkFocused, setLinkFocused] = useState(false);

  // The two calls that actually happen, in order. Rendered verbatim from the
  // copy — the component adds the sequence, never the content.
  const trace = [t('deferred.trace1'), t('deferred.trace2')];

  return (
    <section
      style={{
        padding: isMobile ? '56px 16px' : '80px 32px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: isMobile ? 28 : 56,
            alignItems: 'start',
          }}
        >
          {/* ---- Left: the objection, in their words ---- */}
          <div>
            <div className="t-eyebrow" style={{ marginBottom: 16 }}>
              {t('deferred.eyebrow')}
            </div>
            <h2
              className="t-display"
              style={{
                fontSize: 'clamp(28px, 5vw, 44px)',
                margin: 0,
                maxWidth: 520,
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
            >
              {t('deferred.headline')}
            </h2>

            <blockquote
              style={{
                margin: isMobile ? '20px 0 0' : '24px 0 0',
                padding: '2px 0 2px 16px',
                borderLeft: '2px solid var(--border-strong)',
                fontSize: isMobile ? 15 : 16,
                lineHeight: 1.6,
                color: 'var(--fg-muted)',
              }}
            >
              {t('deferred.objection')}
            </blockquote>

            <p
              style={{
                fontSize: isMobile ? 15 : 17,
                lineHeight: 1.6,
                color: 'var(--fg-muted)',
                margin: '18px 0 0',
              }}
            >
              {t('deferred.body')}
            </p>
          </div>

          {/* ---- Right: the trace that actually runs ---- */}
          <div>
            <ol
              style={{
                listStyle: 'none',
                margin: 0,
                padding: isMobile ? '4px 14px' : '6px 18px',
                background: 'var(--ink-050)',
                border: '1px solid var(--border-strong)',
                borderRadius: 12,
                boxShadow: 'var(--shadow-2)',
              }}
            >
              {trace.map((line, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    padding: isMobile ? '12px 0' : '14px 0',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      flex: 'none',
                      width: 5,
                      height: 5,
                      borderRadius: 999,
                      background: 'var(--fg-dim)',
                      marginTop: isMobile ? 7 : 8,
                    }}
                  />
                  <code
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: 'var(--font-mono)',
                      fontSize: isMobile ? 12 : 13,
                      lineHeight: 1.6,
                      color: 'var(--fg)',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {line}
                  </code>
                </li>
              ))}
            </ol>

            <p
              style={{
                fontSize: isMobile ? 14 : 15,
                lineHeight: 1.6,
                color: 'var(--fg-muted)',
                margin: '16px 0 0',
              }}
            >
              {t('deferred.consequence')}
            </p>
          </div>
        </div>

        {/* ---- Footer rail: the opt-out, and the one place the count appears ---- */}
        <div
          style={{
            marginTop: isMobile ? 24 : 32,
            paddingTop: 20,
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: isMobile ? 8 : 24,
            flexWrap: 'wrap',
          }}
        >
          <p
            style={{
              margin: 0,
              maxWidth: 680,
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              lineHeight: 1.7,
              color: 'var(--fg-dim)',
            }}
          >
            {t('deferred.optOut')}
          </p>

          <a
            href={CATALOG_SOURCE_URLS.tools}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 44,
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              color: 'var(--ice-400)',
              textDecoration: linkHot || linkFocused ? 'underline' : 'none',
              outline: linkFocused ? '2px solid var(--ice-500)' : 'none',
              outlineOffset: 'var(--focus-ring-offset)',
              borderRadius: 4,
            }}
            onMouseEnter={() => setLinkHot(true)}
            onMouseLeave={() => setLinkHot(false)}
            onFocus={(e) => setLinkFocused(e.currentTarget.matches(':focus-visible'))}
            onBlur={() => setLinkFocused(false)}
          >
            {t('deferred.countLink')}
            <Icon.external s={12} />
          </a>
        </div>
      </div>
    </section>
  );
}
